import { Button, Input, Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { IconButton, TruncatedText } from '@/shared/ui';
import { BookmarkIcon, BookmarkPlusIcon } from 'lucide-react';
import { FC, KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TableSelectionDescription, useTablePresets } from '@/entities/settings';
import { cn } from '@/shared/lib';
import { PresetItem } from './PresetItem';

type Props = {
  /**
   * Ключ рабочей записи в `settings.tables`: имя таблицы (например, `'dialogsListV2'`) либо
   * ключ раздела — у таблицы с разделами активный пресет и вид свои у каждого из них.
   */
  sectionKey: string;
  /** Классы обёртки области пресета — именно она сжимается по доступному месту, а не кнопка */
  className?: string;
  /**
   * Классы скрытых распорок, задающих запас ширины под самое длинное название. Запас нужен не
   * всегда: снимающий его класс (`hidden`) оставляет ширину области по названию активного пресета.
   * Условие, при котором запас нужен, знает раскладка вызывающей строки, а не сам поповер.
   */
  widthReserveClassName?: string;
  /**
   * Пользователь выбрал пресет в списке: его id либо `null`, если пресет снят.
   * Задан — применение берёт на себя вызывающая сторона, а сам поповер состояние таблицы не
   * меняет: у пресета может быть собственный раздел со своим адресом и своим видом (список
   * диалогов), и решение, к чьему виду относится выбор, принимается снаружи.
   */
  onPresetSelect?: (presetId: string | null) => void;
  /**
   * Описание выборки, лежащей в текущем виде, — пресет, сохранённый из вида, уносит его с собой;
   * `null` — выборки в виде нет или её описание неизвестно
   */
  selectionDescription?: TableSelectionDescription | null;
};

/**
 * Поповер управления именованными пресетами настроек таблицы.
 *
 * - Список пресетов — системных, совместных и личных — с подсветкой активного: клик по строке
 *   применяет пресет.
 * - Инлайн-форма «Сохранить как новый» — создаёт личный пресет из текущего состояния таблицы.
 *
 * Управление применённым пресетом (переименование, закрепление, удаление, снятие) живёт рядом
 * с кнопкой «Пресеты» ({@link PresetActions}), а не в строках списка.
 *
 * Запись донастройки в активный пресет живёт не здесь, а иконочной кнопкой в строке поиска рядом
 * с отменой изменений (см. `DialogListDataGrid`) — так её видно сразу, без открытия списка.
 *
 * Активный пресет персистируется в `tables[sectionKey].activePresetId` и сохраняется
 * между сессиями через стандартный механизм синхронизации UiSettings с бекендом.
 */
export const TablePresetsPopover: FC<Props> = ({
  sectionKey,
  className,
  widthReserveClassName,
  onPresetSelect,
  selectionDescription,
}) => {
  const { t } = useTranslation(['common', 'tablePresets']);
  const { presets, activePreset, isDirty, savePresetAs, apply } = useTablePresets(sectionKey);

  const [isOpen, setIsOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const handleApply = useCallback(
    (presetId: string) => {
      if (onPresetSelect) {
        onPresetSelect(presetId);
        return;
      }
      apply(presetId);
    },
    [apply, onPresetSelect],
  );

  const handleCreate = useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    savePresetAs(trimmed, { description: selectionDescription });
    setNewName('');
  }, [newName, savePresetAs, selectionDescription]);

  const handleNewNameKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreate();
      }
    },
    [handleCreate],
  );

  const triggerLabel = activePreset?.name ?? t('tablePresets:triggerLabel');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {/*
        Сжимается обёртка, а не сама кнопка: у `Button` из kit базовый `ibs:shrink-0`, и снять его
        классом `shrink` нельзя — проектный `cn` это голый twMerge без префикса `ibs`, для него
        `shrink` и `ibs:shrink-0` не конфликтуют, оба остаются в `class`, а победитель зависит от
        порядка правил в собранном CSS. Обёртка сжимается по флексу штатно, `max-w-full` держит
        кнопку в её границах — оба класса с базовыми классами kit не конкурируют.
      */}
      {/* Подсказки на триггере нет: она всплывала поверх открытого поповера. */}
      <div className={cn('flex min-w-0', className)}>
        <PopoverTrigger asChild>
          <Button
            variant={activePreset ? 'outline' : 'ghost'}
            size="sm"
            icon={<BookmarkIcon className="size-4" />}
            aria-label={t('tablePresets:triggerLabel')}
            className="max-w-full"
          >
            {/*
              Ширина области — по самому длинному названию пресета, а не по активному: скрытые
              распорки в той же ячейке грида задают её max-content, поэтому переключение пресета
              не двигает переключатели поиска. Предел ширины задаёт контейнер строки поиска.
              Убранные из потока распорки (`widthReserveClassName`) возвращают ширину к активному
              названию — там, где двигать соседей нечем.
            */}
            <span className="grid min-w-0">
              {presets.map((preset) => (
                <span
                  key={preset.id}
                  aria-hidden
                  className={cn('col-start-1 row-start-1 h-0 invisible overflow-hidden', widthReserveClassName)}
                >
                  {preset.name}
                </span>
              ))}
              <TruncatedText className="col-start-1 row-start-1" tooltip={triggerLabel}>
                {triggerLabel}
                {isDirty && <span className="ml-1 text-muted-foreground">{'*'}</span>}
              </TruncatedText>
            </span>
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        align="end"
        className="min-w-72 p-3 flex flex-col gap-3"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <h4 className="font-medium text-sm">{t('tablePresets:title')}</h4>

        {presets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">{t('tablePresets:empty')}</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-72 overflow-auto">
            {presets.map((preset) => (
              <PresetItem
                key={preset.id}
                preset={preset}
                isActive={preset.id === activePreset?.id}
                onApply={() => handleApply(preset.id)}
              />
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">{t('tablePresets:createHint')}</p>
          <div className="flex gap-2">
            <Input
              size="sm"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={handleNewNameKeyDown}
              placeholder={t('tablePresets:newPresetPlaceholder')}
              className="flex-1"
            />
            <IconButton
              variant="default"
              size="sm"
              icon={<BookmarkPlusIcon className="size-4" />}
              disabled={!newName.trim()}
              onClick={handleCreate}
              label={t('tablePresets:create')}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
